import type { Parameters } from '@medplum/fhirtypes';
import { APP_PERMISSIONS } from '@sf2/fhir-types/constants/app-permissions.js';
import type { AppPermission } from '@sf2/fhir-types/constants/app-permissions.js';
import { DEFAULT_ORGANIZATION_FEATURE_FLAGS } from '@sf2/fhir-types/constants/feature-flags.js';
import type {
  OrganizationFeatureFlagKey,
  OrganizationFeatureFlags,
} from '@sf2/fhir-types/constants/feature-flags.js';

import { medplum } from '@/config/medplum';
import { queryClient } from '@/config/queryClient';

export { APP_PERMISSIONS };
export type { AppPermission };

export type PermissionsResponse = {
  role: string;
  permissions: string[];
  featureFlags: OrganizationFeatureFlags;
};

export const permissionsQueryKey = (practitionerId: string | undefined) => [
  'user-permissions',
  practitionerId,
];

export function parsePermissionsResponse(params: Parameters): PermissionsResponse {
  const parameters = params.parameter ?? [];
  const role = parameters.find((p) => p.name === 'role')?.valueString ?? '';
  const permissions = parameters
    .filter((p) => p.name === 'permission')
    .map((p) => p.valueString ?? '')
    .filter(Boolean);

  const featureFlags: OrganizationFeatureFlags = { ...DEFAULT_ORGANIZATION_FEATURE_FLAGS };
  for (const p of parameters) {
    if (p.name !== 'featureFlag') continue;
    const key = p.part?.find((x) => x.name === 'key')?.valueString;
    const enabled = p.part?.find((x) => x.name === 'enabled')?.valueBoolean ?? false;
    if (key && key in featureFlags) {
      featureFlags[key as OrganizationFeatureFlagKey] = enabled;
    }
  }

  return { role, permissions, featureFlags };
}

export async function getUserPermissions(): Promise<PermissionsResponse> {
  const profile = medplum.getProfile();
  if (!profile || profile.resourceType !== 'Practitioner') {
    return { role: '', permissions: [], featureFlags: { ...DEFAULT_ORGANIZATION_FEATURE_FLAGS } };
  }

  return queryClient.ensureQueryData({
    queryKey: permissionsQueryKey(profile.id),
    queryFn: async () => {
      const result = (await medplum.get(
        medplum.fhirUrl('User', '$get-permissions').toString(),
      )) as Parameters;
      return parsePermissionsResponse(result);
    },
  });
}

export function hasPermission(permissions: string[], permission: AppPermission): boolean {
  return permissions.includes(APP_PERMISSIONS.SUPERADMIN) || permissions.includes(permission);
}
